"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { isFirebaseMessagingConfigValid } from "@ui/hooks/useFirebaseNotifications/firebaseConfig";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";

import { InfoBanner } from "../InfoBanner";

import { useBrowserWebPushOptOutSync } from "./useBrowserWebPushOptOutSync";
import { useWebPushFirebaseNotifications } from "./useWebPushFirebaseNotifications";
import {
  hasWebPushStorageIdentity,
  isEligiblePathname,
  isWebPushConfigValid,
  isWebPushPlatformEligible,
} from "./webPushEligibility";
import {
  getPromptAvailability,
  recordWebPushAttempt,
  recordWebPushSubscriptionRetry,
  writeWebPushPromptState,
} from "./webPushPromptState";

import styles from "./WebPushNotificationsAutoPromptController.module.scss";

type WebPushNotificationsAutoPromptControllerProps = {
  pathname?: string;
};

const notificationsAcceptedBanner = "isWebPushNotificationsSuccessBannerShown";
const notificationsDeclinedBanner = "isWebPushNotificationsDeclinedBannerShown";

export const WebPushNotificationsAutoPromptController = ({
  pathname,
}: WebPushNotificationsAutoPromptControllerProps) => {
  const requestInFlight = useRef(false);
  const [bannerToShow, setBannerToShow] = useState<"successBanner" | "declinedBanner">();
  const { isUserLoggedIn } = useUser();
  const config = useConfig();
  const { __ } = useTranslations();
  const showSuccessBanner = useCallback(() => setBannerToShow("successBanner"), []);
  const showDeclinedBanner = useCallback(() => setBannerToShow("declinedBanner"), []);
  const { handleFailedSubscription, requestPermissionAndGetToken, syncBrowserOptOut } = useWebPushFirebaseNotifications(
    {
      webPushConsentId: config.webPushConsentId,
      onExplicitDecline: showDeclinedBanner,
      onSubscriptionSuccess: showSuccessBanner,
    }
  );

  useBrowserWebPushOptOutSync({
    isUserLoggedIn,
    onFail: handleFailedSubscription,
    syncBrowserOptOut,
  });

  useEffect(() => {
    const promptConfig = config.webPushAutoPromptConfig;
    const currentPathname = pathname ?? window.location.pathname;
    const isConfigValid = isWebPushConfigValid(promptConfig);
    const hasConsentId = Boolean(config.webPushConsentId);
    const hasStorageIdentity = hasWebPushStorageIdentity(config.EVO_TENANT, config.localeCode);
    const isFirebaseConfigured =
      Boolean(config.isFirebaseEnabled) &&
      isFirebaseMessagingConfigValid(config.firebaseSdkCredentials, config.firebaseVapidKey);
    const isPlatformEligible = isWebPushPlatformEligible();
    const isPathnameEligible = isConfigValid && isEligiblePathname(currentPathname, config.localeCode, promptConfig);
    const isPromptEligible =
      config.isWebPushAutoPromptEnabled === true &&
      isConfigValid &&
      hasConsentId &&
      hasStorageIdentity &&
      isFirebaseConfigured &&
      isUserLoggedIn &&
      isPlatformEligible &&
      isPathnameEligible;

    if (!isPromptEligible) return;

    const handleDocumentClick = () => {
      if (requestInFlight.current) return;

      const now = Date.now();
      const currentAvailability = getPromptAvailability({
        config: promptConfig,
        tenant: config.EVO_TENANT,
        locale: config.localeCode,
        permission: Notification.permission,
        now,
      });

      if (!currentAvailability.eligible) return;

      let currentState = currentAvailability.state;
      try {
        currentState =
          Notification.permission === "default"
            ? recordWebPushAttempt(currentAvailability.storageKey, currentAvailability.state, now)
            : recordWebPushSubscriptionRetry(currentAvailability.storageKey, currentAvailability.state, now);
      } catch {
        return;
      }

      requestInFlight.current = true;
      void requestPermissionAndGetToken()
        .then((result) => {
          if (result === "granted") {
            try {
              writeWebPushPromptState(currentAvailability.storageKey, { ...currentState, subscribed: true });
            } catch {
              // Browser permission remains the source of truth when storage becomes unavailable.
            }
          } else if (result === "denied") {
            try {
              writeWebPushPromptState(currentAvailability.storageKey, { ...currentState, denied: true });
            } catch {
              // Browser permission remains the source of truth when storage becomes unavailable.
            }
          }
        })
        .catch(handleFailedSubscription)
        .finally(() => {
          requestInFlight.current = false;
        });
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [config, handleFailedSubscription, isUserLoggedIn, pathname, requestPermissionAndGetToken]);

  return (
    <>
      <InfoBanner
        bannerType="green"
        content={__("web_notifications.subscription.success")}
        ctaLabel={__("web_notifications.subscription.success_cta")}
        ctaLink={config.webPushLearnMoreSuccessLink ?? ""}
        storageKey={notificationsAcceptedBanner}
        showInfoBanner={bannerToShow === "successBanner"}
        dataTestId="acceptedNotificationsBanner"
        hideCta={!config.webPushLearnMoreSuccessLink}
        className={styles.InfoBannerWebNotification}
      />
      <InfoBanner
        bannerType="amber"
        content={__("web_notifications.subscription.declined")}
        ctaLabel={__("web_notifications.subscription.declined_cta")}
        ctaLink={config.webPushLearnMoreDeclineLink ?? ""}
        storageKey={notificationsDeclinedBanner}
        showInfoBanner={bannerToShow === "declinedBanner"}
        dataTestId="declinedNotificationsBanner"
        hideCta={!config.webPushLearnMoreDeclineLink}
        className={styles.InfoBannerWebNotification}
      />
    </>
  );
};
